import React, { useState, useEffect } from 'react';
import './ManageTeachers.css';
import collegeConfig from '../../config/collegeConfig';
import { savePDF } from '../../utils/savePDF';
import {
  FaEdit, FaTrash, FaEye, FaUser, FaPhone, FaSearch, FaTimes, FaSave,
  FaBook, FaKey, FaUserPlus, FaChalkboardTeacher, FaMobileAlt, FaBuilding,
  FaCrown, FaUpload, FaCheckSquare, FaSquare, FaFileExcel, FaTrashAlt,
  FaFilePdf, FaGraduationCap
} from 'react-icons/fa';

const ManageTeachers = () => {
  const [teachers, setTeachers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [ugSubjects, setUgSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [selectedTeacherIds, setSelectedTeacherIds] = useState([]);
  const [bulkUploadData, setBulkUploadData] = useState([]);
  const [uploadResults, setUploadResults] = useState(null);
  const [bulkAssignSearch, setBulkAssignSearch] = useState('');
  
  const [formData, setFormData] = useState({
    email: '', password: '', firstName: '', lastName: '', department: '',
    departmentCourseId: '', phone: '', mobile: '', dateOfBirth: '', isHOD: false,
    designation: '', teachesUG: false, assignedUGSubjectIds: [],
    isActive: true, leavingDate: '', thesisSignedTillDate: '',
    permanentAddress: { street: '', city: '', state: '', postalCode: '', country: '' }
  });

  const DESIGNATION_OPTIONS = [
    'Professor', 'Associate Professor', 'Assistant Professor',
    'Senior Resident', 'Junior Resident', 'Tutor', 'Demonstrator', 'Lecturer'
  ];

  const [assignmentData, setAssignmentData] = useState({
    courseId: '', batches: []
  });
  
  const [batchInput, setBatchInput] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    fetchTeachers();
    fetchCourses();
    fetchUGSubjects();
  }, []);

  const fetchTeachers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/teachers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTeachers(data.teachers || []);
      } else {
        setError('Failed to fetch teachers');
      }
    } catch (error) {
      console.error('Error fetching teachers:', error);
      setError('Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/courses`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCourses(data.courses || []);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchUGSubjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/ug/subjects`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUgSubjects(data.subjects || []);
      }
    } catch (error) {
      console.error('Error fetching UG subjects:', error);
    }
  };

  const extractDepartmentName = (courseName) => {
    if (!courseName) return '';
    return courseName
      .replace(/^M\.?D\.?\s*/i, '')
      .replace(/^M\.?S\.?\s*/i, '')
      .replace(/^M\.?B\.?B\.?S\.?\s*/i, '')
      .replace(/\s+COURSE$/i, '')
      .trim();
  };

  const handleDepartmentChange = (courseId) => {
    const selectedCourse = courses.find(c => c._id === courseId);
    if (selectedCourse) {
      const departmentName = extractDepartmentName(selectedCourse.name);
      setFormData({ ...formData, department: departmentName, departmentCourseId: courseId });
    } else {
      setFormData({ ...formData, department: '', departmentCourseId: '' });
    }
  };

  const generatePassword = () => {
    const letters = 'abcdefghijklmnopqrstuvwxyz';
    let password = '';
    for (let i = 0; i < 6; i++) {
      password += letters.charAt(Math.floor(Math.random() * letters.length));
    }
    return password;
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.match(/\.(xlsx|xls)$/)) {
      setError('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    try {
      const XLSX = await import('xlsx');
      const reader = new FileReader();
      
      reader.onload = (event) => {
        try {
          const workbook = XLSX.read(event.target.result, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          const teachers = [];
          
          for (let i = 1; i < jsonData.length; i++) {
            const row = jsonData[i];
            
            // Skip empty rows
            if (!row || row.length === 0 || !row[0]) continue;
            
            let firstName, lastName, email, phone = null;
            const password = generatePassword();
            
            if (row.length >= 2) {
              // Check if column 2 (index 1) contains an email address
              const secondColumn = row[1] ? row[1].toString().trim() : '';
              const isEmailInSecondColumn = secondColumn.includes('@');
              
              if (isEmailInSecondColumn) {
                // Format 2: FullName, Email, Phone (3 columns)
                const fullName = row[0] ? row[0].toString().trim() : '';
                email = row[1] ? row[1].toString().trim().toLowerCase() : '';
                
                // Split the full name into first and last name
                if (fullName) {
                  const nameParts = fullName.trim().split(/\s+/);
                  
                  if (nameParts.length === 1) {
                    // Single word - use for both first and last name
                    firstName = nameParts[0];
                    lastName = nameParts[0];
                  } else if (nameParts.length === 2) {
                    // Two words - first and last
                    firstName = nameParts[0];
                    lastName = nameParts[1];
                  } else {
                    // Three or more words - last word is last name, rest is first name
                    lastName = nameParts[nameParts.length - 1];
                    firstName = nameParts.slice(0, -1).join(' ');
                  }
                }
                
                // Column 3 (index 2): Phone (optional)
                if (row[2]) {
                  phone = row[2].toString().trim();
                }
                
              } else {
                // Format 1: FirstName, LastName, Email, Phone (4 columns)
                firstName = row[0] ? row[0].toString().trim() : '';
                lastName = row[1] ? row[1].toString().trim() : '';
                email = row[2] ? row[2].toString().trim().toLowerCase() : '';
                
                // Column 4 (index 3): Phone (optional)
                if (row[3]) {
                  phone = row[3].toString().trim();
                }
              }
              
              // Validate required fields
              if (firstName && lastName && email) {
                const teacherData = {
                  firstName,
                  lastName,
                  email,
                  password
                };
                
                if (phone) {
                  teacherData.phone = phone;
                }
                
                teachers.push(teacherData);
              }
            }
          }
          
          if (teachers.length === 0) {
            setError('No valid teacher data found in file. Please check the format.');
            return;
          }
          
          setBulkUploadData(teachers);
          setSuccess(`Parsed ${teachers.length} teachers from file`);
        } catch (error) {
          console.error('Error parsing Excel:', error);
          setError('Failed to parse Excel file');
        }
      };
      
      reader.readAsBinaryString(file);
    } catch (error) {
      console.error('Error loading Excel library:', error);
      setError('Failed to load Excel parser');
    }
  };

  const generateCredentialsPDF = async (successfulTeachers) => {
    try {
      const jsPDF = (await import('jspdf')).default;
      const doc = new jsPDF();
      
      doc.setFontSize(18);
      doc.text('Teacher Credentials', 14, 20);
      doc.setFontSize(10);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 28);
      
      let yPosition = 40;
      const lineHeight = 8;
      const pageHeight = doc.internal.pageSize.height;
      
      successfulTeachers.forEach((teacher, index) => {
        if (yPosition > pageHeight - 30) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(`${index + 1}. ${teacher.firstName} ${teacher.lastName}`, 14, yPosition);
        yPosition += lineHeight;
        
        doc.setFont(undefined, 'normal');
        doc.setFontSize(10);
        doc.text(`Email: ${teacher.email}`, 20, yPosition);
        yPosition += lineHeight;
        doc.text(`Password: ${teacher.password}`, 20, yPosition);
        yPosition += lineHeight + 4;
      });
      
      await savePDF(doc, 'teacher-credentials.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Failed to generate PDF');
    }
  };

  const handleBulkUpload = async () => {
    if (bulkUploadData.length === 0) {
      setError('Please upload a file first');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/teachers/bulk-upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ teachers: bulkUploadData })
      });

      const data = await response.json();

      if (response.ok) {
        setUploadResults(data.results);
        setSuccess(data.message);
        
        if (data.results.success.length > 0) {
          await generateCredentialsPDF(data.results.success);
        }
        
        fetchTeachers();
      } else {
        setError(data.message || 'Bulk upload failed');
      }
    } catch (error) {
      console.error('Error in bulk upload:', error);
      setError('Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  const getUnassignedTeachers = () => {
    return teachers.filter(teacher => 
      !teacher.courses || teacher.courses.length === 0
    );
  };

  const getFilteredUnassignedTeachers = () => {
    const unassigned = getUnassignedTeachers();
    if (!bulkAssignSearch) return unassigned;
    
    const searchLower = bulkAssignSearch.toLowerCase();
    return unassigned.filter(teacher => {
      const fullName = `${teacher.profile.firstName} ${teacher.profile.lastName}`.toLowerCase();
      const email = teacher.email.toLowerCase();
      return fullName.includes(searchLower) || email.includes(searchLower);
    });
  };

  const toggleTeacherSelection = (teacherId) => {
    setSelectedTeacherIds(prev => {
      if (prev.includes(teacherId)) {
        return prev.filter(id => id !== teacherId);
      } else {
        return [...prev, teacherId];
      }
    });
  };

  const toggleSelectAllUnassigned = () => {
    const filteredUnassigned = getFilteredUnassignedTeachers();
    if (selectedTeacherIds.length === filteredUnassigned.length && filteredUnassigned.length > 0) {
      setSelectedTeacherIds([]);
    } else {
      setSelectedTeacherIds(filteredUnassigned.map(t => t._id));
    }
  };

  const handleBulkAssignCourse = async () => {
    if (selectedTeacherIds.length === 0) {
      setError('Please select at least one teacher');
      return;
    }

    if (!assignmentData.courseId) {
      setError('Please select a course');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const selectedCourse = courses.find(c => c._id === assignmentData.courseId);
      
      // First, update all selected teachers' departments
      if (selectedCourse) {
        const departmentName = extractDepartmentName(selectedCourse.name);
        
        for (const teacherId of selectedTeacherIds) {
          await fetch(`${collegeConfig.api.baseURL}/teachers/${teacherId}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              department: departmentName
            })
          });
        }
      }
      
      // Then bulk assign course
      const response = await fetch(`${collegeConfig.api.baseURL}/teachers/bulk-assign-course`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          teacherIds: selectedTeacherIds,
          courseId: assignmentData.courseId,
          batches: assignmentData.batches
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message);
        setSelectedTeacherIds([]);
        setShowBulkAssign(false);
        setBulkAssignSearch('');
        setAssignmentData({ courseId: '', batches: [] });
        fetchTeachers();
      } else {
        setError(data.message || 'Bulk assign failed');
      }
    } catch (error) {
      console.error('Error in bulk assign:', error);
      setError('Error connecting to server');
    }
  };

  const toggleSelectAllForDelete = () => {
    if (selectedTeacherIds.length === filteredTeachers.length) {
      setSelectedTeacherIds([]);
    } else {
      setSelectedTeacherIds(filteredTeachers.map(t => t._id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTeacherIds.length === 0) {
      setError('Please select at least one teacher');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedTeacherIds.length} teacher(s)? This will remove them from all courses.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/teachers/bulk-delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ teacherIds: selectedTeacherIds })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message);
        setSelectedTeacherIds([]);
        setShowBulkDelete(false);
        fetchTeachers();
      } else {
        setError(data.message || 'Bulk delete failed');
      }
    } catch (error) {
      console.error('Error in bulk delete:', error);
      setError('Error connecting to server');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const url = modalMode === 'create' 
        ? `${collegeConfig.api.baseURL}/teachers`
        : `${collegeConfig.api.baseURL}/teachers/${selectedTeacher._id}`;
      
      const method = modalMode === 'create' ? 'POST' : 'PUT';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        const teacherId = modalMode === 'create' ? data.teacher._id : selectedTeacher._id;

        if (formData.departmentCourseId) {
          await fetch(
            `${collegeConfig.api.baseURL}/teachers/${teacherId}/assign-course`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                courseId: formData.departmentCourseId,
                batches: []
              })
            }
          );
        }

        // Always reconcile UG subject assignment (even to an empty list) so
        // unchecking a subject — or unchecking "teaches UG" entirely —
        // actually clears stale assignments instead of leaving them behind.
        await fetch(
          `${collegeConfig.api.baseURL}/teachers/${teacherId}/assign-ug-subjects`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              subjectIds: formData.teachesUG ? formData.assignedUGSubjectIds : []
            })
          }
        );
        
        setSuccess(data.message);
        fetchTeachers();
        closeModal();
      } else {
        setError(data.message || 'Operation failed');
      }
    } catch (error) {
      console.error('Error saving teacher:', error);
      setError('Error connecting to server');
    }
  };

  const handleDelete = async (teacherId) => {
    if (!window.confirm('Are you sure you want to permanently delete this teacher? This will remove them from all courses and cannot be undone.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/teachers/${teacherId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setSuccess('Teacher deleted permanently');
        fetchTeachers();
      } else {
        setError('Failed to delete teacher');
      }
    } catch (error) {
      console.error('Error deleting teacher:', error);
      setError('Error connecting to server');
    }
  };

  const handleAssignCourse = async () => {
    if (!assignmentData.courseId) {
      setError('Please select a course');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // Get the selected course to set department
      const selectedCourse = courses.find(c => c._id === assignmentData.courseId);
      
      // First update the teacher's department
      if (selectedCourse) {
        const departmentName = extractDepartmentName(selectedCourse.name);
        await fetch(`${collegeConfig.api.baseURL}/teachers/${selectedTeacher._id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            department: departmentName
          })
        });
      }
      
      // Then assign the course
      const response = await fetch(
        `${collegeConfig.api.baseURL}/teachers/${selectedTeacher._id}/assign-course`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(assignmentData)
        }
      );
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess('Course assigned successfully');
        fetchTeachers();
        closeModal();
      } else {
        setError(data.message || 'Failed to assign course');
      }
    } catch (error) {
      console.error('Error assigning course:', error);
      setError('Error connecting to server');
    }
  };

  const handleRemoveCourse = async (teacherId, courseId) => {
    if (!window.confirm('Remove teacher from this course?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${collegeConfig.api.baseURL}/teachers/${teacherId}/remove-course/${courseId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        setSuccess('Teacher removed from course');
        fetchTeachers();
      } else {
        setError('Failed to remove teacher from course');
      }
    } catch (error) {
      console.error('Error removing course:', error);
      setError('Error connecting to server');
    }
  };

  const handlePasswordReset = async () => {
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${collegeConfig.api.baseURL}/teachers/${selectedTeacher._id}/reset-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ newPassword })
        }
      );
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess('Password reset successfully');
        setShowPasswordReset(false);
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setError(data.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      setError('Error connecting to server');
    }
  };

  const openModal = (mode, teacher = null) => {
    setModalMode(mode);
    setSelectedTeacher(teacher);
    setError('');
    setShowPasswordReset(false);
    
    if (mode === 'create') {
      setFormData({
        email: '', password: '', firstName: '', lastName: '', department: '',
        departmentCourseId: '', phone: '', mobile: '', dateOfBirth: '', isHOD: false,
        designation: '', teachesUG: false, assignedUGSubjectIds: [],
        isActive: true, leavingDate: '', thesisSignedTillDate: '',
        permanentAddress: { street: '', city: '', state: '', postalCode: '', country: '' }
      });
    } else if (teacher) {
      let matchingCourseId = '';
      if (teacher.profile.department) {
        const matchingCourse = courses.find(course => {
          const courseDeptName = extractDepartmentName(course.name);
          return courseDeptName.toLowerCase() === teacher.profile.department.toLowerCase();
        });
        if (matchingCourse) {
          matchingCourseId = matchingCourse._id;
        }
      }
      
      setFormData({
        email: teacher.email,
        password: '',
        firstName: teacher.profile.firstName,
        lastName: teacher.profile.lastName,
        department: teacher.profile.department || '',
        departmentCourseId: matchingCourseId,
        phone: teacher.profile.phone || '',
        mobile: teacher.profile.mobile || '',
        dateOfBirth: teacher.profile.dateOfBirth ? 
          new Date(teacher.profile.dateOfBirth).toISOString().split('T')[0] : '',
        isHOD: teacher.profile.isHOD || false,
        designation: teacher.profile.designation || '',
        teachesUG: teacher.profile.teachesUG || false,
        assignedUGSubjectIds: (teacher.assignedUGSubjects || []).map(s => s._id),
        isActive: teacher.isActive,
        leavingDate: teacher.leavingDate ? new Date(teacher.leavingDate).toISOString().split('T')[0] : '',
        thesisSignedTillDate: teacher.thesisSignedTillDate ? new Date(teacher.thesisSignedTillDate).toISOString().split('T')[0] : '',
        permanentAddress: teacher.profile.permanentAddress || {
          street: '', city: '', state: '', postalCode: '', country: ''
        }
      });
    }
    
    if (mode === 'assign') {
      setAssignmentData({ courseId: '', batches: [] });
      setBatchInput('');
    }
    
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedTeacher(null);
    setShowPasswordReset(false);
    setNewPassword('');
    setConfirmPassword('');
    setError('');
  };

  const closeBulkUploadModal = () => {
    setShowBulkUpload(false);
    setBulkUploadData([]);
    setUploadResults(null);
    setError('');
  };

  const closeBulkAssignModal = () => {
    setShowBulkAssign(false);
    setSelectedTeacherIds([]);
    setBulkAssignSearch('');
    setAssignmentData({ courseId: '', batches: [] });
    setBatchInput('');
    setError('');
  };

  const closeBulkDeleteModal = () => {
    setShowBulkDelete(false);
    setSelectedTeacherIds([]);
    setError('');
  };

  const addBatch = () => {
    if (batchInput.trim()) {
      setAssignmentData({
        ...assignmentData,
        batches: [...assignmentData.batches, batchInput.trim()]
      });
      setBatchInput('');
    }
  };

  const removeBatch = (index) => {
    setAssignmentData({
      ...assignmentData,
      batches: assignmentData.batches.filter((_, i) => i !== index)
    });
  };

  const filteredTeachers = teachers.filter(teacher => {
    const fullName = `${teacher.profile.firstName} ${teacher.profile.lastName}`.toLowerCase();
    const email = teacher.email.toLowerCase();
    const searchLower = searchTerm.toLowerCase();
    return fullName.includes(searchLower) || email.includes(searchLower);
  });

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  if (loading) {
    return (
      <div className="manage-teachers-loading">
        <div className="spinner"></div>
        <p>Loading teachers...</p>
      </div>
    );
  }

  return (
    <div className="manage-teachers-container">
      <div className="manage-teachers-header">
        <div className="header-content">
          <h2>Manage Teachers</h2>
          <p>Add, edit, and manage teacher accounts and course assignments</p>
        </div>
        <div className="header-actions">
          <button className="btn-bulk-upload" onClick={() => setShowBulkUpload(true)}>
            <FaUpload /> Bulk Upload
          </button>
          <button className="btn-bulk-assign" onClick={() => setShowBulkAssign(true)}>
            <FaCheckSquare /> Bulk Assign
          </button>
          <button className="btn-bulk-delete" onClick={() => setShowBulkDelete(true)}>
            <FaTrashAlt /> Bulk Delete
          </button>
          <button className="btn-create-teacher" onClick={() => openModal('create')}>
            <FaUserPlus /> Add Teacher
          </button>
        </div>
      </div>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="teachers-controls">
        <div className="search-bar">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search teachers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="teachers-stats">
          <div className="stat-badge">
            <FaChalkboardTeacher />
            <span>{teachers.length} Teachers</span>
          </div>
          <div className="stat-badge">
            <FaBook />
            <span>{courses.length} Courses</span>
          </div>
        </div>
      </div>

      <div className="teachers-grid">
        {filteredTeachers.length === 0 ? (
          <div className="no-teachers">
            <FaChalkboardTeacher className="no-teachers-icon" />
            <h3>No teachers found</h3>
            <p>Add your first teacher to get started</p>
          </div>
        ) : (
          filteredTeachers.map(teacher => (
            <div key={teacher._id} className="teacher-card">
              <div className="teacher-card-header">
                <div className="teacher-avatar"><FaUser /></div>
                <div className="teacher-info">
                  <h3>
                    {teacher.profile.firstName} {teacher.profile.lastName}
                    {teacher.profile.isHOD && (
                      <span className="hod-badge" title="Head of Department">
                        <FaCrown /> HOD
                      </span>
                    )}
                    {teacher.profile.teachesUG && (
                      <span className="hod-badge" title="Also teaches UG courses">
                        <FaGraduationCap /> UG
                      </span>
                    )}
                  </h3>
                  {teacher.profile.designation && (
                    <p className="teacher-designation">{teacher.profile.designation}</p>
                  )}
                  <p className="teacher-email">{teacher.email}</p>
                </div>
                <div className="teacher-actions">
                  <button className="btn-icon btn-view" onClick={() => openModal('view', teacher)}>
                    <FaEye />
                  </button>
                  <button className="btn-icon btn-edit" onClick={() => openModal('edit', teacher)}>
                    <FaEdit />
                  </button>
                  <button className="btn-icon btn-assign" onClick={() => openModal('assign', teacher)}>
                    <FaBook />
                  </button>
                  <button className="btn-icon btn-delete" onClick={() => handleDelete(teacher._id)}>
                    <FaTrash />
                  </button>
                </div>
              </div>
              
              <div className="teacher-card-body">
                <div className="teacher-details">
                  {teacher.profile.department && (
                    <div className="detail-item">
                      <FaBuilding className="detail-icon" />
                      <span>{teacher.profile.department}</span>
                    </div>
                  )}
                  {teacher.profile.phone && (
                    <div className="detail-item">
                      <FaPhone className="detail-icon" />
                      <span>{teacher.profile.phone}</span>
                    </div>
                  )}
                  {teacher.profile.mobile && (
                    <div className="detail-item">
                      <FaMobileAlt className="detail-icon" />
                      <span>{teacher.profile.mobile}</span>
                    </div>
                  )}
                </div>
                
                {teacher.courses && teacher.courses.length > 0 && (
                  <div className="teacher-courses">
                    <h4>Courses ({teacher.courses.length})</h4>
                    <div className="courses-list">
                      {teacher.courses.map(course => (
                        <div key={course._id} className="course-tag">
                          <span>{course.name}</span>
                          <button
                            className="remove-course"
                            onClick={() => handleRemoveCourse(teacher._id, course._id)}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="teacher-status">
                  <span className={`status-badge ${teacher.isActive ? 'active' : 'inactive'}`}>
                    {teacher.isActive ? 'Active' : 'Inactive'}
                  </span>
                  {teacher.leavingDate && (
                    <span style={{ marginLeft: 8, fontSize: 12, color: '#b45309' }}>
                      Leaving {new Date(teacher.leavingDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {showBulkUpload && (
        <div className="modal-overlay" onClick={closeBulkUploadModal}>
          <div className="modal-content admin-bulk-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FaFileExcel /> Bulk Upload Teachers</h3>
              <button className="modal-close" onClick={closeBulkUploadModal}>
                <FaTimes />
              </button>
            </div>
            
            <div className="bulk-upload-content">
              <div className="upload-instructions">
                <h4>Instructions:</h4>
                <ul>
                  <li>Upload an Excel file (.xlsx or .xls)</li>
                  <li>First row should be headers</li>
                  <li><strong>Format 1:</strong></li>
                  <ul>
                    <li>Column 1: First Name (Required)</li>
                    <li>Column 2: Last Name (Required)</li>
                    <li>Column 3: Email (Required)</li>
                    <li>Column 4: Phone (Optional)</li>
                  </ul>
                  <li><strong>Format 2:</strong></li>
                  <ul>
                    <li>Column 1: Full Name (Required)</li>
                    <li>Column 2: Email (Required)</li>
                    <li>Column 3: Phone (Optional)</li>
                  </ul>
                  <li>A random 6-letter password will be generated</li>
                  <li>Download PDF with credentials after upload</li>
                </ul>
              </div>
              
              <div className="upload-section">
                <label htmlFor="excel-upload" className="upload-label">
                  <FaUpload />
                  <span>Choose Excel File</span>
                  <input
                    id="excel-upload"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                  />
                </label>
                
                {bulkUploadData.length > 0 && (
                  <div className="upload-preview">
                    <h4>Preview ({bulkUploadData.length} teachers):</h4>
                    <div className="preview-list">
                      {bulkUploadData.slice(0, 5).map((teacher, index) => (
                        <div key={index} className="preview-item">
                          <span><strong>{teacher.firstName} {teacher.lastName}</strong></span>
                          <span>{teacher.email}</span>
                          <span>Password: {teacher.password}</span>
                          {teacher.phone && <span>Phone: {teacher.phone}</span>}
                        </div>
                      ))}
                      {bulkUploadData.length > 5 && (
                        <p className="preview-more">...and {bulkUploadData.length - 5} more</p>
                      )}
                    </div>
                  </div>
                )}
                
                {uploadResults && (
                  <div className="upload-results">
                    <h4>Upload Results:</h4>
                    <div className="results-summary">
                      <div className="result-success">
                        <strong>{uploadResults.success.length}</strong> teachers uploaded
                        {uploadResults.success.length > 0 && (
                          <p><FaFilePdf /> PDF downloaded with credentials</p>
                        )}
                      </div>
                      {uploadResults.failed.length > 0 && (
                        <div className="result-failed">
                          <strong>{uploadResults.failed.length}</strong> failed
                          <div className="failed-list">
                            {uploadResults.failed.map((fail, index) => (
                              <div key={index} className="failed-item">
                                <span>{fail.data.email}</span>
                                <span className="fail-reason">{fail.reason}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={handleBulkUpload}
                  className="btn-primary"
                  disabled={bulkUploadData.length === 0}
                >
                  <FaSave /> Upload Teachers
                </button>
                <button type="button" className="btn-secondary" onClick={closeBulkUploadModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showBulkAssign && (
        <div className="modal-overlay" onClick={closeBulkAssignModal}>
          <div className="modal-content admin-bulk-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FaCheckSquare /> Bulk Assign Course</h3>
              <button className="modal-close" onClick={closeBulkAssignModal}>
                <FaTimes />
              </button>
            </div>
            
            <div className="bulk-assign-content">
              <div className="unenrolled-info">
                <p>Showing unassigned teachers ({getUnassignedTeachers().length} teachers)</p>
              </div>
              
              <div className="bulk-assign-search">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search teachers..."
                  value={bulkAssignSearch}
                  onChange={(e) => setBulkAssignSearch(e.target.value)}
                />
              </div>
              
              <div className="selection-controls">
                <button className="btn-select-all" onClick={toggleSelectAllUnassigned}>
                  {selectedTeacherIds.length === getFilteredUnassignedTeachers().length && getFilteredUnassignedTeachers().length > 0 ? 
                    <><FaCheckSquare /> Deselect All</> : 
                    <><FaSquare /> Select All</>
                  }
                </button>
                <span className="selection-count">
                  {selectedTeacherIds.length} selected
                </span>
              </div>
              
              <div className="students-selection-list">
                {getFilteredUnassignedTeachers().length === 0 ? (
                  <div className="no-unenrolled">
                    <p>No unassigned teachers</p>
                  </div>
                ) : (
                  getFilteredUnassignedTeachers().map(teacher => (
                    <div 
                      key={teacher._id} 
                      className={`selection-item ${selectedTeacherIds.includes(teacher._id) ? 'selected' : ''}`}
                      onClick={() => toggleTeacherSelection(teacher._id)}
                    >
                      <div className="selection-checkbox">
                        {selectedTeacherIds.includes(teacher._id) ? 
                          <FaCheckSquare /> : <FaSquare />
                        }
                      </div>
                      <div className="selection-info">
                        <strong>{teacher.profile.firstName} {teacher.profile.lastName}</strong>
                        <span>{teacher.email}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              <div className="assign-form">
                <h4>Course Details:</h4>
                
                <div className="form-group2">
                  <label>Select Course *</label>
                  <select
                    value={assignmentData.courseId}
                    onChange={(e) => setAssignmentData({...assignmentData, courseId: e.target.value})}
                  >
                    <option value="">-- Select course --</option>
                    {courses.map(course => (
                      <option key={course._id} value={course._id}>
                        {course.name} ({course.code})
                      </option>
                    ))}
                  </select>
                  <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                    Teacher department will be set to the selected course
                  </small>
                </div>
                
                <div className="form-group2">
                  <label>Batches (Optional)</label>
                  <div className="batch-input-group">
                    <input
                      type="text"
                      placeholder="e.g., 2024-A"
                      value={batchInput}
                      onChange={(e) => setBatchInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addBatch())}
                    />
                    <button type="button" onClick={addBatch} className="btn-add-batch">
                      Add
                    </button>
                  </div>
                  {assignmentData.batches.length > 0 && (
                    <div className="batches-list">
                      {assignmentData.batches.map((batch, index) => (
                        <span key={index} className="batch-tag">
                          {batch}
                          <button onClick={() => removeBatch(index)}>
                            <FaTimes />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={handleBulkAssignCourse}
                  className="btn-primary"
                  disabled={selectedTeacherIds.length === 0 || !assignmentData.courseId}
                >
                  <FaSave /> Assign Course
                </button>
                <button type="button" className="btn-secondary" onClick={closeBulkAssignModal}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showBulkDelete && (
        <div className="modal-overlay" onClick={closeBulkDeleteModal}>
          <div className="modal-content admin-bulk-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FaTrashAlt /> Bulk Delete Teachers</h3>
              <button className="modal-close" onClick={closeBulkDeleteModal}>
                <FaTimes />
              </button>
            </div>
            
            <div className="bulk-assign-content">
              <div className="delete-warning">
                <p><strong>Warning:</strong> This will delete selected teachers and remove them from all courses.</p>
              </div>
              
              <div className="selection-controls">
                <button className="btn-select-all" onClick={toggleSelectAllForDelete}>
                  {selectedTeacherIds.length === filteredTeachers.length ? 
                    <><FaCheckSquare /> Deselect All</> : 
                    <><FaSquare /> Select All</>
                  }
                </button>
                <span className="selection-count">
                  {selectedTeacherIds.length} selected
                </span>
              </div>
              
              <div className="students-selection-list">
                {filteredTeachers.map(teacher => (
                  <div 
                    key={teacher._id} 
                    className={`selection-item ${selectedTeacherIds.includes(teacher._id) ? 'selected' : ''}`}
                    onClick={() => toggleTeacherSelection(teacher._id)}
                  >
                    <div className="selection-checkbox">
                      {selectedTeacherIds.includes(teacher._id) ? 
                        <FaCheckSquare /> : <FaSquare />
                      }
                    </div>
                    <div className="selection-info">
                      <strong>{teacher.profile.firstName} {teacher.profile.lastName}</strong>
                      <span>{teacher.email}</span>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={handleBulkDelete}
                  className="btn-danger"
                  disabled={selectedTeacherIds.length === 0}
                >
                  <FaTrashAlt /> Delete Selected
                </button>
                <button type="button" className="btn-secondary" onClick={closeBulkDeleteModal}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {modalMode === 'create' ? 'Add Teacher' : 
                 modalMode === 'edit' ? 'Edit Teacher' : 
                 modalMode === 'assign' ? 'Assign Course' : 'Teacher Details'}
              </h3>
              <button className="modal-close" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>
            
            {modalMode === 'assign' ? (
              <div className="assign-form">
                <div className="form-group2">
                  <label>Select Course *</label>
                  <select
                    value={assignmentData.courseId}
                    onChange={(e) => setAssignmentData({...assignmentData, courseId: e.target.value})}
                  >
                    <option value="">-- Select course --</option>
                    {courses.map(course => (
                      <option key={course._id} value={course._id}>
                        {course.name} ({course.code})
                      </option>
                    ))}
                  </select>
                  <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                    Teacher department will be set to the selected course
                  </small>
                </div>
                
                <div className="form-group2">
                  <label>Batches (Optional)</label>
                  <div className="batch-input-group">
                    <input
                      type="text"
                      placeholder="e.g., 2024-A"
                      value={batchInput}
                      onChange={(e) => setBatchInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addBatch())}
                    />
                    <button type="button" onClick={addBatch} className="btn-add-batch">
                      Add
                    </button>
                  </div>
                  {assignmentData.batches.length > 0 && (
                    <div className="batches-list">
                      {assignmentData.batches.map((batch, index) => (
                        <span key={index} className="batch-tag">
                          {batch}
                          <button onClick={() => removeBatch(index)}>
                            <FaTimes />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="form-actions">
                  <button 
                    type="button" 
                    onClick={handleAssignCourse}
                    className="btn-primary"
                  >
                    <FaSave /> Assign Course
                  </button>
                  <button type="button" className="btn-secondary" onClick={closeModal}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="teacher-form">
                <div className="form-row">
                  <div className="form-group2">
                    <label>First Name *</label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                      required
                      disabled={modalMode === 'view'}
                    />
                  </div>
                  
                  <div className="form-group2">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                      required
                      disabled={modalMode === 'view'}
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group2">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      disabled={modalMode === 'view'}
                    />
                  </div>
                  
                  {modalMode === 'create' && (
                    <div className="form-group2">
                      <label>Password *</label>
                      <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        required
                        placeholder="Min 6 characters"
                      />
                    </div>
                  )}
                </div>
                
                <div className="form-row">
                  <div className="form-group2">
                    <label>Department (Course) *</label>
                    <select
                      value={formData.departmentCourseId}
                      onChange={(e) => handleDepartmentChange(e.target.value)}
                      required
                      disabled={modalMode === 'view'}
                    >
                      <option value="">-- Select Department --</option>
                      {courses.map(course => (
                        <option key={course._id} value={course._id}>
                          {course.name}
                        </option>
                      ))}
                    </select>
                    {formData.department && (
                      <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                        Department: {formData.department}
                      </small>
                    )}
                  </div>
                  
                  <div className="form-group2">
                    <label>Date of Birth</label>
                    <input
                      type="date"
                      value={formData.dateOfBirth}
                      onChange={(e) => setFormData({...formData, dateOfBirth: e.target.value})}
                      disabled={modalMode === 'view'}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group2">
                    <label>Designation</label>
                    <select
                      value={formData.designation}
                      onChange={(e) => setFormData({...formData, designation: e.target.value})}
                      disabled={modalMode === 'view'}
                    >
                      <option value="">-- Select Designation --</option>
                      {DESIGNATION_OPTIONS.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {formData.department && modalMode !== 'view' && (
                  <div className="form-row">
                    <div className="form-group2 full-width">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.isHOD}
                          onChange={(e) => setFormData({...formData, isHOD: e.target.checked})}
                        />
                        <span>Assign as Head of Department (HOD)</span>
                      </label>
                      {formData.isHOD && (
                        <p className="hod-notice">
                          <FaCrown /> This teacher will be HOD for {formData.department}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {modalMode !== 'view' && (
                  <div className="form-row">
                    <div className="form-group2 full-width">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.teachesUG}
                          onChange={(e) => setFormData({
                            ...formData,
                            teachesUG: e.target.checked,
                            assignedUGSubjectIds: e.target.checked ? formData.assignedUGSubjectIds : []
                          })}
                        />
                        <span>Also teaches UG (undergraduate) courses</span>
                      </label>

                      {formData.teachesUG && (
                        <div className="ug-subject-assign-list">
                          <p className="ug-subject-assign-hint">
                            Assigned UG subjects (only this teacher's assigned
                            subjects' entries will be visible for review):
                          </p>
                          {ugSubjects.length === 0 ? (
                            <p className="ug-subject-assign-hint">No UG subjects exist yet.</p>
                          ) : (
                            ugSubjects.map(subject => (
                              <label key={subject._id} className="checkbox-label ug-subject-assign-item">
                                <input
                                  type="checkbox"
                                  checked={formData.assignedUGSubjectIds.includes(subject._id)}
                                  onChange={(e) => {
                                    const next = e.target.checked
                                      ? [...formData.assignedUGSubjectIds, subject._id]
                                      : formData.assignedUGSubjectIds.filter(id => id !== subject._id);
                                    setFormData({ ...formData, assignedUGSubjectIds: next });
                                  }}
                                />
                                <span>{subject.name} ({subject.code})</span>
                              </label>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group2">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      disabled={modalMode === 'view'}
                    />
                  </div>
                  
                  <div className="form-group2">
                    <label>Mobile</label>
                    <input
                      type="tel"
                      value={formData.mobile}
                      onChange={(e) => setFormData({...formData, mobile: e.target.value})}
                      disabled={modalMode === 'view'}
                    />
                  </div>
                </div>
                
                <div className="form-section">
                  <h4>Permanent Address</h4>
                  <div className="form-row">
                    <div className="form-group2 full-width">
                      <label>Street</label>
                      <input
                        type="text"
                        value={formData.permanentAddress.street}
                        onChange={(e) => setFormData({
                          ...formData,
                          permanentAddress: {...formData.permanentAddress, street: e.target.value}
                        })}
                        disabled={modalMode === 'view'}
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group2">
                      <label>City</label>
                      <input
                        type="text"
                        value={formData.permanentAddress.city}
                        onChange={(e) => setFormData({
                          ...formData,
                          permanentAddress: {...formData.permanentAddress, city: e.target.value}
                        })}
                        disabled={modalMode === 'view'}
                      />
                    </div>
                    
                    <div className="form-group2">
                      <label>State</label>
                      <input
                        type="text"
                        value={formData.permanentAddress.state}
                        onChange={(e) => setFormData({
                          ...formData,
                          permanentAddress: {...formData.permanentAddress, state: e.target.value}
                        })}
                        disabled={modalMode === 'view'}
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group2">
                      <label>Postal Code</label>
                      <input
                        type="text"
                        value={formData.permanentAddress.postalCode}
                        onChange={(e) => setFormData({
                          ...formData,
                          permanentAddress: {...formData.permanentAddress, postalCode: e.target.value}
                        })}
                        disabled={modalMode === 'view'}
                      />
                    </div>
                    
                    <div className="form-group2">
                      <label>Country</label>
                      <input
                        type="text"
                        value={formData.permanentAddress.country}
                        onChange={(e) => setFormData({
                          ...formData,
                          permanentAddress: {...formData.permanentAddress, country: e.target.value}
                        })}
                        disabled={modalMode === 'view'}
                      />
                    </div>
                  </div>
                </div>
                
                {modalMode === 'edit' && (
                  <div className="form-group2 full-width">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                      />
                      <span>Active Account</span>
                    </label>
                    <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                      {formData.isActive ? 'Teacher can log in and access the system' : 'Teacher account is deactivated'}
                    </small>
                  </div>
                )}

                {modalMode === 'edit' && (
                  <div className="form-group2 full-width">
                    <label>Leaving Date (optional)</label>
                    <input
                      type="date"
                      value={formData.leavingDate}
                      onChange={(e) => setFormData({...formData, leavingDate: e.target.value})}
                    />
                    <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                      If this faculty is leaving the institution, set the date here. Once set, this faculty is hidden from the monitor's team list, and their leaving date is shown wherever their name still appears elsewhere. This does not affect login access.
                    </small>
                  </div>
                )}

                {modalMode === 'edit' && (
                  <div className="form-group2 full-width">
                    <label>Thesis Signed Till (optional)</label>
                    <input
                      type="date"
                      value={formData.thesisSignedTillDate}
                      onChange={(e) => setFormData({...formData, thesisSignedTillDate: e.target.value})}
                    />
                    <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                      Students will see "thesis signed till {'{'}this date{'}'}" wherever this faculty is listed as a thesis guide.
                    </small>
                  </div>
                )}

                {modalMode === 'edit' && !showPasswordReset && (
                  <div className="password-reset-section">
                    <button 
                      type="button" 
                      onClick={() => setShowPasswordReset(true)}
                      className="btn-reset-password"
                    >
                      <FaKey /> Reset Password
                    </button>
                  </div>
                )}
                
                {showPasswordReset && (
                  <div className="password-reset-form">
                    <h4>Reset Password</h4>
                    <div className="form-row">
                      <div className="form-group2">
                        <label>New Password</label>
                        <input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Min 6 characters"
                        />
                      </div>
                      
                      <div className="form-group2">
                        <label>Confirm Password</label>
                        <input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="password-actions">
                      <button 
                        type="button" 
                        onClick={handlePasswordReset}
                        className="btn-confirm-reset"
                      >
                        Reset Password
                      </button>
                      <button 
                        type="button" 
                        onClick={() => {
                          setShowPasswordReset(false);
                          setNewPassword('');
                          setConfirmPassword('');
                        }}
                        className="btn-cancel-reset"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                
                {modalMode !== 'view' && !showPasswordReset && (
                  <div className="form-actions">
                    <button type="submit" className="btn-primary">
                      <FaSave /> {modalMode === 'create' ? 'Create' : 'Update'}
                    </button>
                    <button type="button" className="btn-secondary" onClick={closeModal}>
                      Cancel
                    </button>
                  </div>
                )}
                
                {modalMode === 'view' && selectedTeacher && (
                  <div className="view-info">
                    <div className="info-section">
                      <h4>Account Information</h4>
                      <p><strong>Status:</strong> <span className={`status-text ${selectedTeacher.isActive ? 'active' : 'inactive'}`}>
                        {selectedTeacher.isActive ? 'Active' : 'Inactive'}
                      </span></p>
                      {selectedTeacher.leavingDate && (
                        <p><strong>Leaving Date:</strong> {new Date(selectedTeacher.leavingDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                      )}
                      {selectedTeacher.thesisSignedTillDate && (
                        <p><strong>Thesis Signed Till:</strong> {new Date(selectedTeacher.thesisSignedTillDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                      )}
                      {selectedTeacher.profile.isHOD && (
                        <p><strong>Position:</strong> <span className="hod-text">
                          <FaCrown /> Head of Department
                        </span></p>
                      )}
                      {selectedTeacher.profile.designation && (
                        <p><strong>Designation:</strong> {selectedTeacher.profile.designation}</p>
                      )}
                      <p><strong>Created:</strong> {new Date(selectedTeacher.createdAt).toLocaleDateString()}</p>
                    </div>
                    
                    {selectedTeacher.courses && selectedTeacher.courses.length > 0 && (
                      <div className="info-section">
                        <h4>Assigned Courses</h4>
                        <div className="assigned-courses-list">
                          {selectedTeacher.courses.map(course => (
                            <div key={course._id} className="assigned-course-item">
                              <span className="course-name">{course.name}</span>
                              <span className="course-code">{course.code}</span>
                              <span className="course-duration">{course.duration} months</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageTeachers;